package org.teddapp.project.db.repo

import app.cash.sqldelight.coroutines.asFlow
import app.cash.sqldelight.coroutines.mapToList
import com.tedd.db.Loan
import com.tedd.db.Loan_payment
import com.tedd.db.TeddDatabase
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.withContext
import org.teddapp.project.db.newId
import org.teddapp.project.db.nowMillis

/** Les deux sens d'un prêt. */
object LoanDirection {
    /** J'ai prêté : une créance, on me doit. */
    const val LENT = "lent"
    /** On m'a prêté : une dette, je dois. */
    const val BORROWED = "borrowed"
}

/** Les types de transaction qu'un prêt écrit. */
object LoanTx {
    const val LOAN_GIVEN = "loan_given"
    const val LOAN_RECEIVED = "loan_received"
    const val REPAYMENT_RECEIVED = "repayment_received"
    const val REPAYMENT_PAID = "repayment_paid"
    val ALL = setOf(LOAN_GIVEN, LOAN_RECEIVED, REPAYMENT_RECEIVED, REPAYMENT_PAID)
}

/**
 * Le dû et le restant d'un prêt : une règle, un seul endroit, lue par le dépôt comme par
 * l'interface qui a déjà les remboursements sous la main.
 */
object LoanMath {
    /** Le dû : capital plus intérêts. */
    fun due(loan: Loan): Long = loan.principal + loan.interest

    /** Ce qui reste à rembourser, d'après les remboursements vivants de ce prêt. */
    fun remaining(loan: Loan, payments: List<Loan_payment>): Long =
        due(loan) - payments.filter { it.loan_id == loan.id && it.is_deleted == 0L }.sumOf { it.amount }

    /** Part remboursée, de 0 à 1, pour la barre. */
    fun paidFraction(loan: Loan, payments: List<Loan_payment>): Float {
        val due = due(loan)
        if (due <= 0L) return 1f
        return ((due - remaining(loan, payments)).toFloat() / due.toFloat()).coerceIn(0f, 1f)
    }
}

/**
 * Les prêts, dans les deux sens, et leurs remboursements.
 *
 * Le dû et le restant viennent de [LoanMath] ; le prêt est soldé quand le restant tombe à
 * zéro. Chaque
 * mouvement d'argent est une transaction de type dédié, écrite dans la même transaction
 * SQL que le prêt ou le remboursement, et l'effet sur le solde passe par
 * [FinanceTransactionRepository.applyEffectsNow].
 */
class LoanRepository(private val db: TeddDatabase) {

    fun observeAll(): Flow<List<Loan>> =
        db.loanQueries.selectAll().asFlow().mapToList(Dispatchers.Default)

    fun observeAllPayments(): Flow<List<Loan_payment>> =
        db.loanPaymentQueries.selectAll().asFlow().mapToList(Dispatchers.Default)

    fun paymentsOf(loanId: String): List<Loan_payment> =
        db.loanPaymentQueries.selectByLoan(loanId).executeAsList()

    /** Ce qui reste à rembourser. */
    fun remaining(loan: Loan): Long = LoanMath.remaining(loan, paymentsOf(loan.id))

    /**
     * Crée un prêt. Avec [accountId], l'argent a bougé : prêter débite le compte, emprunter
     * le crédite, et le prêt porte la transaction. Sans compte, rien n'a bougé : une
     * facture due, un service rendu.
     */
    suspend fun create(
        direction: String,
        counterparty: String,
        principal: Long,
        interest: Long = 0L,
        currency: String = "XOF",
        date: String,
        dueDate: String? = null,
        note: String? = null,
        accountId: String? = null,
        id: String = newId("loan"),
    ): String = withContext(Dispatchers.Default) {
        require(direction == LoanDirection.LENT || direction == LoanDirection.BORROWED) { "sens inconnu : $direction" }
        require(principal > 0L) { "le capital doit être positif" }
        require(interest >= 0L) { "les intérêts ne peuvent pas être négatifs" }
        val now = nowMillis()
        val name = counterparty.trim()
        db.transaction {
            val txId = accountId?.let { acc ->
                val type = if (direction == LoanDirection.LENT) LoanTx.LOAN_GIVEN else LoanTx.LOAN_RECEIVED
                val label = if (direction == LoanDirection.LENT) "Prêt à $name" else "Prêt de $name"
                val txId = newId("tx")
                db.financeTransactionQueries.insert(txId, date, acc, null, null, type, principal, label, null, now, now)
                FinanceTransactionRepository.applyEffectsNow(db, type, acc, null, principal, +1, now)
                txId
            }
            val rank = (db.loanQueries.selectAll().executeAsList().maxOfOrNull { it.order_index } ?: -1L) + 1L
            db.loanQueries.insert(
                id, direction, name, principal, interest, currency, date, dueDate, note?.takeIf { it.isNotBlank() },
                accountId, txId, "open", rank, now, now,
            )
        }
        id
    }

    /** Ce qui se corrige après coup : la personne, les intérêts, l'échéance, la note. */
    suspend fun update(id: String, counterparty: String, interest: Long, dueDate: String?, note: String?) =
        withContext(Dispatchers.Default) {
            require(interest >= 0L) { "les intérêts ne peuvent pas être négatifs" }
            val now = nowMillis()
            db.transaction {
                db.loanQueries.update(counterparty.trim(), interest, dueDate, note?.takeIf { it.isNotBlank() }, now, id)
                refreshStatus(id, now)
            }
        }

    /**
     * Un remboursement, toujours par un compte : une créance remboursée crédite, une
     * dette remboursée débite. Refusé au-delà du restant. Rend l'identifiant, ou null si
     * le prêt est inconnu.
     */
    suspend fun addPayment(
        loanId: String,
        amount: Long,
        date: String,
        accountId: String,
        note: String? = null,
        id: String = newId("lp"),
    ): String? = withContext(Dispatchers.Default) {
        val loan = db.loanQueries.selectById(loanId).executeAsOneOrNull() ?: return@withContext null
        require(amount > 0L) { "le remboursement doit être positif" }
        val reste = remaining(loan)
        require(amount <= reste) { "le remboursement dépasse le restant ($reste)" }
        val now = nowMillis()
        db.transaction {
            val lent = loan.direction == LoanDirection.LENT
            val type = if (lent) LoanTx.REPAYMENT_RECEIVED else LoanTx.REPAYMENT_PAID
            val label = if (lent) "Remboursement de ${loan.counterparty}" else "Remboursement à ${loan.counterparty}"
            val txId = newId("tx")
            db.financeTransactionQueries.insert(txId, date, accountId, null, null, type, amount, label, null, now, now)
            FinanceTransactionRepository.applyEffectsNow(db, type, accountId, null, amount, +1, now)
            db.loanPaymentQueries.insert(id, loanId, amount, date, accountId, txId, note?.takeIf { it.isNotBlank() }, now, now)
            refreshStatus(loanId, now)
        }
        id
    }

    /** Retire un remboursement et rétablit le solde du compte. */
    suspend fun deletePayment(payment: Loan_payment) = withContext(Dispatchers.Default) {
        val now = nowMillis()
        db.transaction {
            reverseTx(payment.tx_id, now)
            db.loanPaymentQueries.softDelete(now, now, payment.id)
            refreshStatus(payment.loan_id, now)
        }
    }

    /** Supprime le prêt, ses remboursements et leurs transactions ; les soldes sont rétablis. */
    suspend fun delete(loanId: String) = withContext(Dispatchers.Default) {
        val loan = db.loanQueries.selectById(loanId).executeAsOneOrNull() ?: return@withContext
        val now = nowMillis()
        db.transaction {
            paymentsOf(loanId).forEach { p ->
                reverseTx(p.tx_id, now)
                db.loanPaymentQueries.softDelete(now, now, p.id)
            }
            reverseTx(loan.tx_id, now)
            db.loanQueries.softDelete(now, now, loanId)
        }
    }

    /** Soldé quand plus rien ne reste, ouvert sinon : recalculé après chaque mouvement. */
    private fun refreshStatus(loanId: String, now: Long) {
        val loan = db.loanQueries.selectById(loanId).executeAsOneOrNull() ?: return
        val status = if (remaining(loan) <= 0L) "settled" else "open"
        if (status != loan.status) db.loanQueries.setStatus(status, now, loanId)
    }

    private fun reverseTx(txId: String?, now: Long) {
        val tx = txId?.let { db.financeTransactionQueries.selectById(it).executeAsOneOrNull() } ?: return
        db.financeTransactionQueries.softDelete(now, now, tx.id)
        FinanceTransactionRepository.applyEffectsNow(db, tx.type, tx.account_id, tx.to_account_id, tx.amount, -1, now)
    }
}
